import { Box, Button } from "@mui/material";
import { API_URL, BootstrapTooltip } from "../../utilities";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewIcon from "@mui/icons-material/Preview";
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowModesModel,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import moment from "jalali-moment";
import GridViewMUIX from "../../components/GridViewMUIX";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/auth";
import { MouseEvent, useEffect, useState } from "react";
import { useNotifications } from "@toolpad/core/useNotifications";
import AddIcon from "@mui/icons-material/Add";
import PopoverComponent from "../../components/PopoverComponent";
import NewService from "./new-service";
import SkeltonProgress from "../../components/SkeltonProgress";
import { IAppUser } from "src/interface/IUser";
import { TypeUser } from "src/interface/enum/TypeUser";
import renderCellExpand from "src/GridComponents/GridExpand";
import { ListOfFollowUpType } from "src/interface/enum/FollowUpType";
import { ISystem } from "src/interface/ISystem";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import { getRoutes } from "src/utilities/routes";
import { IService } from "src/interface/IService";
import GroupIcon from "@mui/icons-material/Group"; // آیکون برای گروه‌ها
import ServiceGroupDialog from "./components/ServiceGroupDialog";
import { usePermissions } from "../../utilities/permissions";
import PageTitle from "../../components/PageTitle";
const Services = () => {
  const notifications = useNotifications();
  const { user } = useAuth();
  const { canEdit, canDelete, canCreate } = usePermissions();

  const [rows, setRows] = useState<any>();
  const [openTask, setOpenTask] = useState<boolean>(false);
  const [openGroupDialog, setOpenGroupDialog] = useState<boolean>(false); // حالت برای دیالوگ گروه‌ها
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const Routes = getRoutes();
  const [data, setData] = useState<IService[]>([]);

  const { mutate: getServicesAsync, isPending: isServicesLoading } =
    useMutation({
      mutationFn: async () => {
        const response = await fetch(Routes.ServicesRoutes, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Aceept: "application/json",
          },
        });
        return await response.json();
      },
      onSuccess: (responseData: IService[]) => {
        if (responseData) {
          setData(responseData);
        }
      },
      onError: (error) => {
        notifications.show("خطا در دریافت اطلاعات", {
          severity: "error",
          autoHideDuration: 3000,
        });
        console.log(error);
      },
    });

  const [getSystems, setSystems] = useState<IAutoCompleteLabelValue[]>([]);
  const { mutate: getSystemsAsync, isPending: isSystemsLoading } = useMutation({
    mutationFn: async () => {
      const response = await fetch(Routes.SystemsRoutes, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      });
      return await response.json();
    },
    onSuccess: (responseData: ISystem[]) => {
      if (responseData && responseData.length > 0) {
        setSystems(
          responseData.map((item: ISystem) => ({
            label: item.name,
            value: item.id,
          }))
        );
      }
    },
    onError: (error) => {
      notifications.show("خطا در دریافت اطلاعات", {
        severity: "error",
        autoHideDuration: 3000,
      });
      console.log(error);
    },
  });

  const [getEditors, setEditors] = useState<IAutoCompleteLabelValue[]>();
  const { mutate: getUsersAsync, isPending: isUsersLoading } = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        Routes.UsersRoutes + "getByRole/" + TypeUser.EDITOR,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Aceept: "application/json",
          },
        }
      );
      return await response.json();
    },
    onSuccess: (responseData: IAppUser[]) => {
      if (responseData && responseData.length > 0) {
        setEditors(
          responseData.map((item: IAppUser) => ({
            label: item.fName + " " + item.lName,
            value: item.id,
          }))
        );
      }
    },
    onError: (error) => {
      notifications.show("خطا در دریافت اطلاعات", {
        severity: "error",
        autoHideDuration: 3000,
      });
      console.log(error);
    },
  });

  useEffect(() => {
    if (user?.token) {
      getServicesAsync();
      getSystemsAsync();
      getUsersAsync();
    }
  }, [user?.token]);

  useEffect(() => {
    data && setRows(data);
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "actions",
      type: "actions",
      headerAlign: "center",
      headerName: "عملیات",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "actions",
      minWidth: 150,
      getActions: (params) => {
        const actions = [];

        // نمایش و ویرایش - فقط برای کسانی که دسترسی edit دارند
        if (canEdit("services")) {
          actions.push(
            <BootstrapTooltip title="نمایش و ویرایش" key="edit">
              <GridActionsCellItem
                icon={<PreviewIcon color="primary" />}
                label="نمایش و ویرایش"
                className="textPrimary"
                onClick={handleEditClick(params.id)}
                color="inherit"
              />
            </BootstrapTooltip>
          );
        }

        // حذف - فقط برای کسانی که دسترسی delete دارند
        if (canDelete("services")) {
          actions.push(
            <BootstrapTooltip title="حذف" key="delete">
              <GridActionsCellItem
                icon={<DeleteIcon color="error" />}
                label="حذف"
                onClick={(event) => handleDeleteClick(event, params.id)}
                color="inherit"
              />
            </BootstrapTooltip>
          );
        }

        return actions;
      },
    },
    {
      field: "rowId",
      headerName: "ردیف",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      minWidth: 100,
    },
    {
      field: "name",
      headerName: "نام",
      align: "left",
      headerAlign: "left",
      editable: true,
      sortable: true,
      minWidth: 200,
    },
    {
      field: "price",
      headerName: "قیمت",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      type: "number",
      minWidth: 150,
    },

    {
      field: "isFollowUp",
      headerName: "پیگیری",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      type: "boolean",
      minWidth: 80,
    },
    {
      field: "followUpType",
      headerName: "نوع پیگیری",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      type: "singleSelect",
      valueOptions: ListOfFollowUpType.filter((f) => f.require), // این مقادیر را از لیست FollowUpType قرار دهید
      minWidth: 180,
    },
    {
      field: "firstFollowUpedOn",
      headerName: "تاریخ پیگیری بعدی",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${
          params.value &&
          moment.from(params.value, "en", "YYYY-MM-DD").format("jYYYY/jMM/jDD")
        }`,
      minWidth: 180,
    },

    {
      field: "isPayment",
      headerName: "پرداختی",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      type: "boolean",
      minWidth: 180,
    },

    {
      field: "sampleSMSPayment",
      headerName: "نمونه پیامک پرداخت",
      align: "left",
      headerAlign: "left",
      editable: true,
      sortable: true,
      minWidth: 180,
      renderCell: renderCellExpand,
    },
    {
      field: "systemRef",
      headerName: "سامانه مرتبط",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      minWidth: 180,
      type: "singleSelect",
      valueOptions: getSystems,
    },
    {
      field: "creatoreRef",
      headerName: "کاربر ایجاد کننده",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      minWidth: 180,
      type: "singleSelect",
      valueOptions: getEditors,
      renderCell: (params) => params.value ?? "مدیر",
    },

    {
      field: "createdOn",
      headerName: "تاریخ ایجاد",
      align: "left",
      headerAlign: "left",
      editable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${
          params.value &&
          moment.from(params.value, "en", "YYYY-MM-DD").format("jYYYY/jMM/jDD")
        }`,
      minWidth: 180,
    },
    {
      field: "modifiedOn",
      headerName: "تاریخ ویرایش",
      align: "center",
      headerAlign: "center",
      editable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${
          params.value &&
          moment.from(params.value, "en", "YYYY-MM-DD").format("jYYYY/jMM/jDD")
        }`,
      minWidth: 180,
    },
  ];

  // const { mutateAsync: updateAsync, isPending: isEditLoading } = useMutation({
  //   mutationFn: async (body: any) => {
  //     const response = await fetch(API_URL + "services/" + body.id, {
  //       method: "PUT",
  //       headers: {
  //         Accept: "*/*",
  //         "Content-Type": "application/json-patch+json",
  //         Authorization: `Bearer ${user?.token}`,
  //       },
  //       body: JSON.stringify(body),
  //     });
  //     return await response.json();
  //   },
  //   onSuccess: (responseData) => {
  //     JSON.stringify(responseData);
  //   },
  //   onError: (error) => {
  //     notifications.show("خطا در دریافت اطلاعات", {
  //       severity: "error",
  //       autoHideDuration: 3000,
  //     });
  //     console.log("eee", error);
  //   },
  // });

  const { mutateAsync: deleteAsync, isPending: isDeleteLoading } = useMutation({
    mutationFn: async () => {
      const response = await fetch(API_URL + "services/" + serviceId, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      notifications.show("خطا در دریافت اطلاعات", {
        severity: "error",
        autoHideDuration: 3000,
      });
      console.log(error);
    },
  });

  const [serviceId, setServiceId] = useState<number>();

  const [anchorDeleteEl, setAnchorDeleteEl] =
    useState<HTMLButtonElement | null>(null);
  const openDeletePopover = Boolean(anchorDeleteEl);
  const idDeletePopover = openDeletePopover ? "simple-popover" : undefined;
  const handleDeleteClick = (
    event: MouseEvent<HTMLButtonElement>,
    id: GridRowId
  ) => {
    setAnchorDeleteEl(event.currentTarget);
    setServiceId(id as number);
  };
  const handleDeletePopoverClose = () => {
    setAnchorDeleteEl(null);
  };
  const handleDelete = async () => {
    try {
      const res = await deleteAsync();
      if (res.status) {
        notifications.show("حذف با موفقیت انجام شد!", {
          severity: "success",
          autoHideDuration: 5000,
        });
      } else {
        notifications.show("خطا در حذف!", {
          severity: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.log("🚀 ~ handleDelete ~ error:", error);
      notifications.show("خطا در حذف!", {
        severity: "error",
        autoHideDuration: 5000,
      });
    } finally {
      getServicesAsync();
      handleDeletePopoverClose();
      setServiceId(undefined);
    }
  };

  function EditToolbar() {
    return (
      <GridToolbarContainer>
        {canCreate("services") && (
          <Button
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenTask(true)}
          >
            خدمت جدید
          </Button>
        )}
        {canEdit("services") && (
          <Button
            color="primary"
            startIcon={<GroupIcon />}
            onClick={() => setOpenGroupDialog(true)} // باز کردن دیالوگ گروه‌ها
          >
            مدیریت گروه‌ها
          </Button>
        )}
      </GridToolbarContainer>
    );
  }
  const handleEditClick = (id: GridRowId) => () => {
    setServiceId(id as number);
    setOpenTask(true);
  };

  const handleClose = () => {
    setOpenTask(false);
  };
  const handleGroupDialogClose = () => {
    setOpenGroupDialog(false);
  };
  return (
    <>
      <PageTitle title="خدمات" />
      <PopoverComponent
        caption=" حذف انجام شود؟"
        anchorEl={anchorDeleteEl}
        handlePopoverClose={handleDeletePopoverClose}
        handler={handleDelete}
        id={idDeletePopover}
        isLoading={isDeleteLoading}
        open={openDeletePopover}
        key={"delete"}
      />

      <NewService
        open={openTask}
        handleClose={handleClose}
        isEdit={!!serviceId}
        serviceId={serviceId}
        setServiceId={setServiceId}
        refetch={getServicesAsync}
        // data={rowInfo}
      />
      <ServiceGroupDialog
        open={openGroupDialog}
        handleClose={handleGroupDialogClose}
      />
      <Box
        sx={{
          width: {
            xs: 370, // عرض برای اندازه‌های کوچک
            sm: 500, // عرض برای اندازه‌های متوسط
            md: 720, // عرض برای اندازه‌های بزرگ
            lg: "calc(100% - 80px)", // عرض برای اندازه‌های خیلی بزرگ
            xl: "calc(100% - 80px)", // عرض برای اندازه‌های فوق‌العاده بزرگ
          },
        }}
      >
        {isServicesLoading ? (
          <SkeltonProgress />
        ) : (
          <GridViewMUIX
            gridUniqueName="Services"
            columns={columns}
            loading={isServicesLoading}
            EditToolbar={EditToolbar}
            rowModesModel={rowModesModel}
            sortFields={{ field: "modifiedOn", sort: "desc" }}
            rows={rows}
            setRowModesModel={setRowModesModel}
          />
        )}
      </Box>
    </>
  );
};

export default Services;
